import { postRequestWithRetry } from './asyncRequest';
import { logger } from './logger';
import { AjaxLandingMetricsPublisher } from './metrics';
import { BANNER_TYPE } from 'src/app/slice';

const landingAppEndpoint = '/ccx/ajax/setLanding';
/*
 * NOTE: these two requests previously passed `header` (singular) holding a
 * 'Content-type' value. Nothing ever read that key -- axios only looked at
 * `headers` -- so the value was never sent, which is why the endpoint has always
 * worked: both calls post no body (only `params`), so no Content-Type was needed.
 * Anyone restoring one must add it to `headers`, which postRequestWithRetry now
 * MERGES over the CSRF header per key rather than replacing wholesale, so doing
 * so no longer drops the CSRF token.
 *
 * `responseType: 'none'` because neither caller reads the response. The default
 * would parse the body as JSON, which turns an empty 200 -- what an ack-only
 * endpoint usually answers with -- into a parse failure. axios tolerated that
 * body by falling back to the raw string.
 *
 * `publishEndpointMetrics: true` because the path is the constant above, so the
 * per-endpoint series is a single `<origin>/ccx/ajax/setLanding:POST` for both
 * set and unset, published next to the setRequest/setError counters.
 */

interface SetToastBannerArgs {
  showToastBanner: boolean;
  toastBannerType: BANNER_TYPE;
}

interface SetLandingAppActionProps {
  appName: string;
  updateLandingAppName: (arg: string) => void;
  setToastBanner: (args: SetToastBannerArgs) => void;
}

interface UnsetLandingAppActionProps {
  updateLandingAppName: (arg: string) => void;
  setToastBanner: (args: SetToastBannerArgs) => void;
}

export const setLandingAppAction = async ({
  appName,
  updateLandingAppName,
  setToastBanner,
}: SetLandingAppActionProps): Promise<void> => {
  if (appName.length === 0) {
    return;
  }
  AjaxLandingMetricsPublisher.publishCounterMonitor('setRequest', 1);
  try {
    await postRequestWithRetry({
      params: { landingAppName: appName },
      url: landingAppEndpoint,
      responseType: 'none',
      publishEndpointMetrics: true,
    });
    updateLandingAppName(appName);
    setToastBanner({
      showToastBanner: true,
      toastBannerType: BANNER_TYPE.SUCCESS,
    });
  } catch (error: any) {
    AjaxLandingMetricsPublisher.publishCounterMonitor('setError', 1);
    await logger.error(
      `Fail when setLandingAppAction to endpoint ${landingAppEndpoint} for appName ${appName} with error ${error}`,
    );
    setToastBanner({
      showToastBanner: true,
      toastBannerType: BANNER_TYPE.ERROR,
    });
  }
};

export const unsetLandingAppAction = async ({
  updateLandingAppName,
  setToastBanner,
}: UnsetLandingAppActionProps): Promise<void> => {
  AjaxLandingMetricsPublisher.publishCounterMonitor('unsetRequest', 1);
  try {
    await postRequestWithRetry({
      url: landingAppEndpoint,
      responseType: 'none',
      publishEndpointMetrics: true,
    });
    updateLandingAppName('');
    setToastBanner({
      showToastBanner: true,
      toastBannerType: BANNER_TYPE.SUCCESS,
    });
  } catch (error: any) {
    await logger.error(
      `Fail when unsetLandingAppAction to endpoint ${landingAppEndpoint} with error ${error}`,
    );
    AjaxLandingMetricsPublisher.publishCounterMonitor('unsetError', 1);
    setToastBanner({
      showToastBanner: true,
      toastBannerType: BANNER_TYPE.ERROR,
    });
  }
};
